import { BaseService } from "./base-service";
import { API_BASE_URL } from "../configuration/config";
import { getITOClient } from "../hooks/hooks-helpers";
import { AMZNHAM_CONSUMER } from "../interfaces/constants";
import { GetAuditCommandInput, GetAuditCommandOutput, ITO, Relation } from "@amzn/ito-client";
import { AuditTrail } from "model";

export interface GetAuditTrailsProps {
    orderId: string;
    orderConsumer: string;
    orderRelations: Relation[];
    pageSize: number;
}

export class AuditService extends BaseService {
    private static _instance?: AuditService;
    
    public static get instance(): AuditService {
        if (!AuditService._instance) {
            AuditService._instance = new AuditService(API_BASE_URL);
        }
        return AuditService._instance;
    }
    
    private _itoClientHam?: ITO;
    private _itoClientAmznham?: ITO;

    private get itoClientHam(): ITO {
        if (!this._itoClientHam) {
            this._itoClientHam = getITOClient().client;
        }
        return this._itoClientHam;
    }

    private get itoClientAmznham(): ITO {
        if (!this._itoClientAmznham) {
            this._itoClientAmznham = getITOClient(AMZNHAM_CONSUMER).client;
        }
        return this._itoClientAmznham;
    }

    public async getAuditTrails(props: GetAuditTrailsProps): Promise<AuditTrail[]> {
        //1. Get the audit trails from HAM order
        const hamAudits = await this.callGetAudit(props.orderId, this.itoClientHam, props.pageSize)

        //2. Get the audit trails from AMZNHAM order if it's an Amazoninan order
        let amznhamAudits: GetAuditCommandOutput | undefined = undefined;
        const amznhamOrderId = this.getAmznhamOrderId(props.orderRelations)
        if(props.orderConsumer === AMZNHAM_CONSUMER && amznhamOrderId) {
            amznhamAudits = await this.callGetAudit(amznhamOrderId, this.itoClientAmznham, props.pageSize)
        }

        //3. Transform the audits to table model.
        const hamAuditTrails = this.transformAuditsToAuditTrails(hamAudits);
        const amznhamAuditTrails = this.transformAuditsToAuditTrails(amznhamAudits);

        //4. Merge them together in a single output
        return  [...hamAuditTrails, ...amznhamAuditTrails];
    }

    private async callGetAudit(orderId: string, client: ITO, pageSize: number) {
        const request: GetAuditCommandInput = {
            orderId,
            pageSize,
            nextToken: "0",
        };

        const result = await client.getAudit(request);
        return result;
    }

    private transformAuditsToAuditTrails(audits: GetAuditCommandOutput | undefined): AuditTrail[] {
        if(!audits || !audits.auditEntries) {
            console.log("Empty list of audits to transform");
            return [];
        }

        const auditTrails: AuditTrail[] = [];

        for (const entry of audits.auditEntries) {
            const login = entry.action?.by || '';
            const date = entry.action?.on || undefined;
            const source = entry.action?.via || '';
            const action = entry.actionType || '';

            // Handle CREATE action type
            if (entry.actionType === 'CREATE') {
                const createdValue = entry.createdValue as any;
                let field = 'New Order';
                let newValue = '';

                // Check if it's an attachment
                if (createdValue?.fileName) {
                    field = 'attachment';
                    newValue = createdValue.fileName;
                }
                // Check if it's a comment
                else if (createdValue?.comment) {
                    const isInternal = createdValue.tags && Array.isArray(createdValue.tags) && createdValue.tags.includes('internal');
                    field = isInternal ? 'internal comment' : 'comment';
                    newValue = createdValue.comment;
                }
                // Check if it's an order creation (has orderId)
                else if (createdValue?.orderId) {
                    field = 'New Order';
                    newValue = '';
                }

                auditTrails.push({
                    login,
                    date,
                    action,
                    field,
                    previousValue: '',
                    newValue,
                    source
                });
            } 
            // Handle UPDATE action type with diff array
            else if (entry.diff && Array.isArray(entry.diff)) {
                for (const change of entry.diff) {
                    auditTrails.push({
                        login,
                        date,
                        action: change.type || '',
                        field: change.path || '',
                        previousValue: change.oldValue?.toString() || '',
                        newValue: this.formatValue(change.value),
                        source
                    });
                }
            }
        }

        return auditTrails;
    }

    private formatValue(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value)
                .replace(/,"/g, ',  "')  // Add space after commas before quotes
                .replace(/:/g, ': ');    // Add space after colons
        }
        return value.toString();
    }

    private getAmznhamOrderId(orderRelations: Relation[]): string | undefined {
        return orderRelations.find(relation => 
            relation.id?.startsWith(`ito::${AMZNHAM_CONSUMER}`)
        )?.id;
    }
}
